import { defineComponent as _defineComponent } from 'vue'
import { useAppStore, useCamerasStore, useFloorplansStore, useFloorsStore, useLocationsStore } from '@/stores'
import { getCameraRotationInFloorplan, getCameraRotationInMap, getCursorCoordinates } from '@/service/FloorplanFormulas'
import SettingsSidebar from '@/components/SettingsSidebar.vue'
import AddCamerasSidebar from '../FloorPlan/AddCamerasSidebar.vue'
import { t } from '@/plugins/i18n';
import { computed, nextTick, ref, watch } from 'vue'
import { ApiCameraWithIncludes, FloorResponse } from '@eencloud/eewc-components/src/service/api-types'
import AppHeader from '@/components/AppHeader/AppHeader.vue'
import { goFullScreen } from '@/service/helpers'
import { useZoom } from '@/components/floorplanZoomHook'
import CameraMarker from '@/components/CameraMarker.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'FloorPlanView',
  setup(__props) {

const floorsStore = useFloorsStore();
const floorplansStore = useFloorplansStore();
const camerasStore = useCamerasStore();
const locationsStore = useLocationsStore();
const appStore = useAppStore();

const imageElementContainer = ref();
const imageWrapper = ref();

const imageLoaded = ref<boolean>(false);
const droppedOverFloorplan = ref<boolean>(false);
const imageElement = ref<HTMLImageElement>();
const imageElementHeight = ref(imageElement.value?.height);
const isDragging = ref(false);
let draggingElementCopy: HTMLElement | undefined = undefined;

function onClickCameraMarker(camera: ApiCameraWithIncludes) {
  if (floorsStore.isEditCamerasFloorEnabled) {
    const isClickedCameraEditing = camera.id === (floorsStore.currentEditingCamera || {}).id;

    floorsStore.$patch({
      currentEditingCamera: isClickedCameraEditing ? undefined : camera
    })
  }
}

const { zoomIn, zoomOut, updateDimensions, zoomLevel } = useZoom(imageElement, imageElementContainer);

const checkIsDefaultZoomLevel = computed(() => {
  return zoomLevel.value === 1

})

watch(
  () => zoomLevel.value,
  () => {
    imageElementHeight.value = imageElement.value ? imageElement.value.height : imageElementContainer.value.height
  }
);

function onLoad() {
  updateDimensions();
  imageElementHeight.value = imageElement.value ? imageElement.value.height : imageElementContainer.value.height;
  imageLoaded.value = true;
}



function getCameraCoordinates(dragEvent: DragEvent, floorplanImageWrapperEl: Element, floor: FloorResponse) {
  const floorplanPositionRelativeToWindow = floorplanImageWrapperEl.getBoundingClientRect();
  const dropPositionRelativeToFloorplan = {
    x: dragEvent.clientX - floorplanPositionRelativeToWindow.x,
    y: dragEvent.clientY - floorplanPositionRelativeToWindow.y
  }
  const dropPositionRelativeToFloorplanPercentage = {
    x: dropPositionRelativeToFloorplan.x / floorplanPositionRelativeToWindow.width,
    y: dropPositionRelativeToFloorplan.y / floorplanPositionRelativeToWindow.height,
  }

  if(!floorsStore.currentFloor) {
    return
  }

  return getCursorCoordinates(
    floor,
    dropPositionRelativeToFloorplanPercentage,
    floorplansStore.currentFloorImage
  );
}
function dragend() {
  isDragging.value = false;
  if (droppedOverFloorplan.value) {
    return;
  }

  if (draggingElementCopy) {
    draggingElementCopy.remove();
  }
}

function getRoundedCoordinate(number: number) {
  return Number(number.toFixed(8));
}

async function onDropCamera(dragEvent: DragEvent) {
  droppedOverFloorplan.value = true;

  if (!floorsStore.currentFloor ||
    !locationsStore.currentLocation ||
    !imageWrapper.value ||
    !dragEvent?.dataTransfer?.getData('camera')) {
    return;
  }

  appStore.loading = true;

  const cameraCopy: ApiCameraWithIncludes = JSON.parse(dragEvent.dataTransfer.getData('camera'));
  const cameraCoordinates = getCameraCoordinates(dragEvent, imageWrapper.value, floorsStore.currentFloor);
  let virtualScroll = document.querySelector('.virtual-scroll');
  const lastScrollLevel = virtualScroll?.scrollTop || 0;

  try {
    if (floorsStore.isAddingCamerasToFloorplanActive) {
      await camerasStore.editCamera({
        cameraId: cameraCopy.id,
        cameraData: {
          locationId: locationsStore.currentLocation.id
        },
        devicePosition: {
          longitude: getRoundedCoordinate(cameraCoordinates?.x),
          latitude: getRoundedCoordinate(cameraCoordinates?.y),
          floor: floorsStore.currentFloor.floorLevel
        }
      })
    } else {
      await camerasStore.editCamera({
        cameraId: cameraCopy.id,
        devicePosition: {
          longitude: getRoundedCoordinate(cameraCoordinates?.x),
          latitude: getRoundedCoordinate(cameraCoordinates?.y),
        }
      })
    }
  } catch (e) { /* empty */ }

  draggingElementCopy?.remove();

  droppedOverFloorplan.value = false;
  appStore.loading = false;
  appStore.$patch({ addCamSidebarKey: appStore.addCamSidebarKey+1 });

  await nextTick(()=> {
    virtualScroll = document.querySelector('.virtual-scroll')
    if(virtualScroll?.scrollTo){
      virtualScroll.scrollTo(0, lastScrollLevel);
    }
  });
}

function closeAddingCamerasSidebar() {
  floorsStore.$patch({ isAddingCamerasToFloorplanActive: false });
}
function closeEditSettingsSidebar() {
  floorsStore.$patch({ isEditSettingsActive: false });
}

function onDragStart(event: DragEvent, cameraId: string) {
  const applicationEl = document.getElementsByClassName('v-application')[0] as HTMLElement;

  isDragging.value = true;
  const oldDraggingElement = document.querySelector('.camera-marker-dragging');

  if (oldDraggingElement) {
    oldDraggingElement.remove();
  }

  draggingElementCopy = document.getElementsByClassName(`camera-marker-${cameraId}`)[0].cloneNode(true) as HTMLElement;
  draggingElementCopy.classList.add('camera-marker-dragging');
  draggingElementCopy.style.left = `${event.pageX}px`;
  draggingElementCopy.style.top = `${event.pageY}px`;

  applicationEl.appendChild(draggingElementCopy);

  imageWrapper.value.ondragover = function(evt: DragEvent) {
    if (!draggingElementCopy) {
      return;
    }

    draggingElementCopy.style.left = `${evt.pageX}px`;
    draggingElementCopy.style.top = `${evt.pageY}px`;
  }
}

async function changeCameraRangeAzimuth(camera: ApiCameraWithIncludes, data: { azimuth: number, rangeInMeters: number }) {
  appStore.loading = true;

  if(!floorsStore.currentFloor){
    return
  }

  const cameraRotationInMap = getCameraRotationInMap(
    data.azimuth,
    floorsStore.currentFloor,
    floorplansStore.currentFloorImage
  )

  try{
    await camerasStore.editCamera({
      cameraId: camera.id,
      devicePosition: {
        azimuth: getRoundedCoordinate((cameraRotationInMap + 360) % 360),
        rangeInMeters: data.rangeInMeters
      }
    })
  } catch (error) { /* empty */ }

  appStore.loading = false;
}

function onResize() {
  updateDimensions();
  imageElementHeight.value = imageElement.value ? imageElement.value.height : imageElementContainer.value.height;
}


return { __sfc: true,floorsStore, floorplansStore, camerasStore, locationsStore, appStore, imageElementContainer, imageWrapper, imageLoaded, droppedOverFloorplan, imageElement, imageElementHeight, isDragging, draggingElementCopy, onClickCameraMarker, zoomIn, zoomOut, updateDimensions, zoomLevel, checkIsDefaultZoomLevel, onLoad, getCameraCoordinates, dragend, getRoundedCoordinate, onDropCamera, closeAddingCamerasSidebar, closeEditSettingsSidebar, onDragStart, changeCameraRangeAzimuth, onResize, getCameraRotationInFloorplan, SettingsSidebar, AddCamerasSidebar, t, AppHeader, goFullScreen, CameraMarker }
}

})